import { computed, defineComponent, h, onUnmounted, ref, unref, watch } from "@vue/composition-api";
import { WebRTCConnection } from "@scrile/api-provider/dist/api/WebRTCProvider";
import { StreamingHelper } from "@scrile/streaming-client";
import useDoNotDisturb from "@/hooks/useDoNotDisturb";
import providers from "@/lib/api";
import { isMobile } from "@/lib/browserUtils";
import { parseISO } from "@/lib/time";
import { LoggerContextWithMessage } from "@/modules/callsLogger";
import useLiveChat from "../../hooks/useLiveChat";
import useStreamLogger from "../../hooks/useStreamLogger";
import StreamView from "../StreamView";

export default defineComponent({
  name: "ProduceController",
  props: {
    userId: { type: String, required: true },
  },
  setup(props, { listeners, emit }) {
    const stream = ref<MediaStream>(new MediaStream([]));
    const { setDoNotDisturb } = useDoNotDisturb();
    const connecting = ref(true);
    const connectingState = ref("");
    const connectionData = ref<WebRTCConnection | null>(null);
    const constraints = ref<{ video: MediaTrackConstraints | boolean; audio: MediaTrackConstraints | boolean }>({
      video: true,
      audio: true,
    });
    const WebRTC = new StreamingHelper();

    const { loading, joinData, token, userList, clientServerTimeDiff, errorData } = useLiveChat({
      userId: props.userId,
      connectionDelay: 3000,
    });

    const getLoggerContext = (): Record<string, any> => {
      return {
        stream: { id: stream.value.id, active: stream.value.active },
        connection: connectionData.value,
        constraints: constraints.value,
        joinData: joinData.value,
        errorData: errorData.value,
      };
    };
    const { businessLogicLog, collectErrorLogs, debug } = useStreamLogger("[PRODUCE CONTROLLER]", getLoggerContext);
    WebRTC.logger.on("logging", ({ message, ...others }: LoggerContextWithMessage) =>
      debug(`[STREAM CLIENT] ${message}`, others)
    );

    const dateStart = computed(() => {
      if (!joinData.value) {
        return "";
      }
      return new Date(parseISO(joinData.value.me.joinTime).getTime() + clientServerTimeDiff.value).toISOString();
    });

    watch(errorData, () => {
      if (!errorData) return;
      emit("error", errorData.value);
    });

    const createLocalStream = async () => {
      try {
        stream.value = await WebRTC.getStream(constraints.value);
      } catch (error) {
        collectErrorLogs("Create local stream", error);
      }
    };

    const onCreatedPromise = ref<Promise<void>>();
    const onCreated = async () => {
      try {
        businessLogicLog("Create stream");
        await createLocalStream();
      } catch (error) {
        collectErrorLogs("Create stream", error);
      } finally {
        onCreatedPromise.value = undefined;
      }
    };

    const produceStream = async () => {
      try {
        if (onCreatedPromise.value) await onCreatedPromise.value;
        businessLogicLog("Produce stream");
        connecting.value = true;
        connectionData.value = await providers.LivechatsProvider.broadcast({ token: token.value });
        stream.value = await WebRTC.produceStream(connectionData.value, stream.value);
        setDoNotDisturb(true);
      } catch (error) {
        collectErrorLogs("Produce stream", error);
      } finally {
        connecting.value = false;
      }
    };

    const closeStreams = () => {
      businessLogicLog("Close streams");
      WebRTC.closeAllStreams();
      if (stream.value.getTracks().length > 0) {
        stream.value.getTracks().forEach(t => t.stop());
      }
    };

    const reconnect = async () => {
      businessLogicLog("Reconnect");
      closeStreams();
      await createLocalStream();
      await produceStream();
    };

    const onChangeVideoDevice = async (trackData: MediaTrackConstraints) => {
      businessLogicLog("Change device");
      if (!connectionData.value) {
        return;
      }
      if (isMobile()) {
        stream.value.getVideoTracks().forEach(t => {
          t.stop();
          stream.value.removeTrack(t);
        });
      }
      constraints.value = {
        ...constraints.value,
        video: trackData,
      };
      try {
        stream.value = await WebRTC.produceStream(connectionData.value, { video: trackData, audio: true });
      } catch (error) {
        collectErrorLogs("Change device", error);
      }
    };

    onCreatedPromise.value = onCreated();

    watch(joinData, (joinData, old) => {
      if (!old && joinData) {
        if (!!connectionData.value) {
          reconnect();
        } else {
          produceStream();
        }
      }
    });

    WebRTC.on("connectionstatechange", (state: string) => (connectingState.value = state));

    onUnmounted(() => {
      closeStreams();
      setDoNotDisturb(false);
    });

    return () =>
      h(StreamView, {
        props: {
          produce: true,
          stream: unref(stream),
          connecting: unref(connecting),
          connectingState: unref(connectingState),
          loading: unref(loading),
          userList: unref(userList),
          streamStartedDate: unref(dateStart),
          threadId: joinData.value?.threadId,
          token: joinData.value?.token,
        },
        on: {
          close: listeners.close,
          changeVideoDevice: onChangeVideoDevice,
        },
      });
  },
});
